// Implements: F-PRV-014
/**
 * Class-based syntax highlighting that never turns source into markup.
 *
 * `highlight.js` answers an HTML string. That string is NEVER inserted into the page: it is read
 * back here into a small tree of text and `{className, children}` nodes, which React renders as
 * text nodes and `<span class>` elements. The only markup the library emits is
 * `<span class="hljs-…">`, `</span>` and five escaped characters, so the reader below knows exactly
 * those and treats everything else as text. Classes, not inline styles: the app's `style-src` is
 * `'self'`.
 */
import hljs from "highlight.js/lib/core";
import css from "highlight.js/lib/languages/css";
import javascript from "highlight.js/lib/languages/javascript";
import xml from "highlight.js/lib/languages/xml";

hljs.registerLanguage("xml", xml);
hljs.registerLanguage("css", css);
hljs.registerLanguage("javascript", javascript);

/** Above this many characters a piece is shown as plain text, so the page stays responsive. */
export const HIGHLIGHT_MAX_CHARS = 50_000;

export type SourceLanguage = "html" | "css" | "js";

export interface SpanToken {
  readonly className: string;
  readonly children: readonly Token[];
}
export type Token = string | SpanToken;

const LANGUAGE: Readonly<Record<SourceLanguage, string>> = {
  html: "xml",
  css: "css",
  js: "javascript",
};

const ENTITIES: Readonly<Record<string, string>> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#x27;": "'",
};

function decode(text: string): string {
  return text.replace(/&(?:amp|lt|gt|quot|#x27);/g, (entity) => ENTITIES[entity] ?? entity);
}

const PART = /(<span class="[^"]*">|<\/span>)/;
const OPEN = /^<span class="([^"]*)">$/;

interface Open {
  className: string;
  children: Token[];
}

function readTokens(html: string): Token[] {
  const root: Open = { className: "", children: [] };
  const stack: Open[] = [root];
  const top = (): Open => stack[stack.length - 1] ?? root;
  for (const part of html.split(PART)) {
    if (part === "") continue;
    const open = OPEN.exec(part);
    if (open !== null) {
      stack.push({ className: open[1] ?? "", children: [] });
    } else if (part === "</span>") {
      const done = stack.length > 1 ? stack.pop() : undefined;
      if (done !== undefined) top().children.push({ className: done.className, children: done.children });
    } else {
      top().children.push(decode(part));
    }
  }
  while (stack.length > 1) {
    const done = stack.pop();
    if (done !== undefined) top().children.push({ className: done.className, children: done.children });
  }
  return root.children;
}

export function highlight(code: string, language: SourceLanguage): readonly Token[] {
  if (code.length > HIGHLIGHT_MAX_CHARS) return [code];
  const result = hljs.highlight(code, { language: LANGUAGE[language], ignoreIllegals: true });
  return readTokens(result.value);
}
